import TooltipWrapper from "views/uikit/TooltipWrapper";
import styles from "../style.module.scss";
import { useLocation, useNavigate } from "react-router";
import { connect, useDispatch, useSelector } from "react-redux";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import {
  addToRecentSearches,
  removeFromRecentSearches,
  setCardslistLoading,
  setIsImageUploading,
} from "redux/SingleDataset/actions";
import {
  ENTITY_TYPE_PARAMETER,
  PAGE_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
} from "helpers/constants/miscellaneous";
import { State } from "redux/store";
import {
  getSearchOptions,
  getAutocompleteSuggestions,
  getRecentSearches,
  getIsAutoSuggestQueryEnabled,
  getIsRecentSearchEnabled,
  getAutocompleteLoading,
  getAutocompleteNoResultsFlag,
  getContentLoading,
  getIsCardslistLoading,
  getIsSearchByRelevanceEnabled,
  getRelevanceScores,
} from "redux/SingleDataset/selectors";
import EntityButton from "../EntityTypeFilter";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import axios from "helpers/axiosInstance";
import SearchIcon from "assets/icons/SearchIcon";
import FindSimilarIcon from "assets/icons/FindSimilarIcon";
import classNames from "classnames";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { isValidEntityType, SetStateFunction } from "types";
import RelevanceButton from "./RelevanceButton";
import XIcon from "assets/icons/XIcon";

const SearchContainerContactBanner = ({ loading }: { loading: boolean }) => {
  return !loading ? (
    <div className={styles.searchContainerContactBanner}>
      <div className={styles.textSection}>
        <div className={styles.searchBarIcon}>
          <SearchIcon />
        </div>
        <div
          className={styles.textContainer}
          title="Add labels to enable textual search"
        >
          Add labels to enable textual search
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.contactBannerLoader}></div>
  );
};

const ImageSearchButton = ({
  isImageUploading = false,
}: {
  isImageUploading: boolean;
}) => {
  const dispatch = useDispatch();

  const imageSearchButtonClassnames = classNames(styles.imageSearchButton, {
    [styles.imageUploading]: isImageUploading,
  });

  const handlebuttonClick = () => {
    if (isImageUploading) {
      dispatch(setIsImageUploading(false));
    } else {
      dispatch(setIsImageUploading(true));
    }
  };

  return (
    <div
      className={imageSearchButtonClassnames}
      onClick={handlebuttonClick}
      data-tooltip-content={
        "Temporarily upload an Image to find similar Images in this Dataset"
      }
      data-tooltip-id="data-page-image-upload-button"
      data-tooltip-place="top"
    >
      <FindSimilarIcon />
      {!isImageUploading && (
        <TooltipWrapper id={"data-page-image-upload-button"} maxWidth="254px" />
      )}
    </div>
  );
};

interface SearchContainerProps {
  datasetId: string;
  isLoading: boolean;
  recentSearches: { [id: string]: any[] };
  isImageUploading: boolean;
  hideEntityFilter: boolean;
  imageSearchEnabled: boolean;
  isUserConfigLoading: boolean;
  isClearFiltersCalled: boolean;
  showEntityTypeLoader: boolean;
  isRecentSearchEnabled: boolean;
  isAutoSuggestQueryEnabled: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  setIsClearFiltersCalled: SetStateFunction<boolean>;
}

const SearchContainer = ({
  datasetId,
  isLoading,
  containerRef,
  recentSearches,
  isImageUploading,
  hideEntityFilter,
  imageSearchEnabled,
  isUserConfigLoading,
  showEntityTypeLoader,
  isClearFiltersCalled,
  isRecentSearchEnabled,
  isAutoSuggestQueryEnabled,
  setIsClearFiltersCalled,
}: SearchContainerProps) => {
  const queryParams = useAllQueryParamsFilters();
  const entityTypeFromQueryParam = isValidEntityType(
    queryParams[ENTITY_TYPE_PARAMETER],
  )
    ? queryParams[ENTITY_TYPE_PARAMETER]
    : null;
  const searchOptions = useSelector((state: State) =>
    getSearchOptions(state, entityTypeFromQueryParam),
  );
  const searchEnabled = searchOptions.enabled;

  const getSearchPlaceholder = () => {
    if (searchOptions.semanticSearch) return "Describe what you're looking for";
    if (searchOptions.captionsSearch) return "Search by captions";
    if (searchOptions.labelsSearch) return "Search by labels";
    return "Search by text"; // fallback
  };

  const searchPlaceHolder = getSearchPlaceholder();

  const searchTextParam = queryParams[SEARCH_TEXT_PARAMETER];
  const searchTextThreshold = queryParams[SEARCH_TEXT_THRESHOLD_PARAMETER]
    ? Number(queryParams[SEARCH_TEXT_THRESHOLD_PARAMETER])
    : null;
  const relevanceScores = useSelector((state: any) =>
    getRelevanceScores(state, entityTypeFromQueryParam),
  );
  const [isRecentSearchInFocus, setIsRecentSearchInFocus] = useState(false);
  const [autocompleteSearchText, setAutocompleteSearchText] = useState("");
  const [suggestionFlag, setSuggestionFlag] = useState(false);
  const [inputText, setInputText] = useState(searchTextParam || "");
  const [noResultsFlag, setNoResultsFlag] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [autocompleteTimeout, setAutocompleteTimeout] = useState<number | null>(
    null,
  );
  const [firstLoad, setFirstLoad] = useState(false);
  const recentSearchesRef = useRef<HTMLDivElement>(null);
  const availableSuggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const isSearchTextInParams = !!searchParams.get(SEARCH_TEXT_PARAMETER);
  const showRecentSearches =
    searchEnabled &&
    !!isRecentSearchEnabled &&
    Object.keys(recentSearches).includes(datasetId) &&
    recentSearches[datasetId].length > 0 &&
    !inputText &&
    isRecentSearchInFocus;

  const showAutocompleteSuggestions =
    searchEnabled &&
    isAutoSuggestQueryEnabled &&
    (autocompleteSuggestions.length > 0 ||
      isAutocompleteLoading ||
      noResultsFlag) &&
    !showRecentSearches;

  const isSearchByRelevanceEnabled = useSelector((state: any) =>
    getIsSearchByRelevanceEnabled(state, entityTypeFromQueryParam),
  );

  const showRelevanceButton =
    searchOptions.semanticSearch &&
    isSearchByRelevanceEnabled &&
    searchTextParam;

  const recentSearchArray = showRecentSearches ? recentSearches[datasetId] : [];

  const clearAll = () => {
    setIsRecentSearchInFocus(false);
    setAutocompleteSearchText("");
    setSuggestionFlag(false);
    setInputText("");
    setNoResultsFlag(false);
    setAutocompleteSuggestions([]);
    setIsAutocompleteLoading(false);
  };

  const handleSearchByText = (e: any, clicked?: boolean) => {
    if (e.keyCode === 13 || clicked) {
      if (inputText === searchParams.get(SEARCH_TEXT_PARAMETER)) return;
      if (inputText) {
        dispatch(setCardslistLoading(true));
        searchParams.set(SEARCH_TEXT_PARAMETER, inputText);
        if (
          searchOptions.semanticSearch &&
          isSearchByRelevanceEnabled &&
          relevanceScores.length === 3
        ) {
          searchParams.set(
            SEARCH_TEXT_THRESHOLD_PARAMETER,
            relevanceScores[1].toString(),
          );
        }
        searchParams.set(PAGE_PARAMETER, "1");
        navigate({ search: `?${searchParams.toString()}` });
        if (isRecentSearchEnabled && datasetId) {
          dispatch(addToRecentSearches(datasetId, inputText.trim()) as any);
        }
      }
    }
  };

  const handleAutocompleteSuggestionClick = (suggestion: string) => {
    setIsRecentSearchInFocus(false);
    setSuggestionFlag(true);
    setInputText(suggestion);
    setAutocompleteSuggestions([]);
  };

  const handleRecentSuggestionClick = (suggestion: string) => {
    setIsRecentSearchInFocus(false);
    setSuggestionFlag(true);
    setInputText(suggestion);
    setAutocompleteSuggestions([]);
  };

  const handleRemoveSuggestion = (e: any, suggestion: string) => {
    e.stopPropagation();
    dispatch(removeFromRecentSearches(datasetId, suggestion) as any);
  };

  const handleOutsideClick = (event: any) => {
    const clickedOutsideRecentSearches =
      recentSearchesRef.current &&
      !recentSearchesRef.current.contains(event.target);

    const clickedOutsideAvailableSuggestions =
      availableSuggestionsRef.current &&
      !availableSuggestionsRef.current.contains(event.target);

    const clickedOutsideInput =
      inputRef.current && !inputRef.current.contains(event.target);

    if (
      (clickedOutsideRecentSearches || clickedOutsideAvailableSuggestions) &&
      clickedOutsideInput
    ) {
      setIsRecentSearchInFocus(false);
      setNoResultsFlag(false);
      setAutocompleteSuggestions([]);
      setAutocompleteSearchText("");
    }
  };

  const handleClearSearch = () => {
    searchParams.set(PAGE_PARAMETER, "1");
    clearAll();
    if (searchParams.has(SEARCH_TEXT_PARAMETER)) {
      searchParams.delete(SEARCH_TEXT_PARAMETER);
      searchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);
      navigate({ search: `?${searchParams.toString()}` });
    }
  };

  const fetchAutocompleteSuggestions = async (
    datasetId: string,
    query: string,
  ) => {
    try {
      if (autocompleteTimeout) {
        clearTimeout(autocompleteTimeout);
        setAutocompleteTimeout(null);
      }

      const encodedQuery = encodeURIComponent(query);
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetId}/autocomplete?query=${encodedQuery}`,
      );

      const response = await axios.get(url);
      const suggestions = response.data.results || [];

      if (suggestions.length > 0) {
        setAutocompleteSuggestions(suggestions);
        setNoResultsFlag(false);
      } else {
        setAutocompleteSuggestions([]);
        setNoResultsFlag(true);
      }
      setIsAutocompleteLoading(false);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setAutocompleteSuggestions([]);
      setIsAutocompleteLoading(false);
      setNoResultsFlag(false);
    }
  };

  const handleInputChange = (e: any) => {
    const newValue = e.target.value;
    setAutocompleteSuggestions([]);
    setAutocompleteSearchText("");
    setInputText(newValue);
    setSuggestionFlag(false);

    if (!newValue && (isSearchTextInParams || searchTextThreshold)) {
      searchParams.delete(SEARCH_TEXT_PARAMETER);
      searchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);
      navigate({ search: `?${searchParams.toString()}` });
    }
  };

  const handleSuggestions = () => {
    const handleHeader = () => {
      if (isAutocompleteLoading && autocompleteSuggestions.length < 1) {
        return <div className={styles.autocompleteLoadingText}>Loading...</div>;
      } else if (!isAutocompleteLoading && noResultsFlag) {
        return (
          <div className={styles.autocompleteLoadingText}>No results found</div>
        );
      } else if (autocompleteSuggestions.length > 0) {
        return <div className={styles.autocompleteLoadingText}>Results</div>;
      }
    };
    return (
      <>
        {handleHeader()}
        {autocompleteSuggestions.map((suggestion: string, index: number) => {
          const queryIndex = suggestion
            .toLowerCase()
            .indexOf(autocompleteSearchText.toLowerCase());

          if (queryIndex === -1) {
            return (
              <div
                key={index}
                className={styles.autocompleteItem}
                onClick={() => handleAutocompleteSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            );
          }

          const beforeQueryText = suggestion.substring(0, queryIndex);
          const queryText = suggestion.substring(
            queryIndex,
            queryIndex + autocompleteSearchText.length,
          );
          const afterQueryText = suggestion.substring(
            queryIndex + autocompleteSearchText.length,
          );

          const showRoundCorners =
            index === autocompleteSuggestions.length - 1 && index >= 4
              ? styles.lastSuggestionItem
              : "";

          return (
            <div
              key={index}
              className={`${styles.autocompleteItem} ${showRoundCorners}`}
              onClick={() => handleAutocompleteSuggestionClick(suggestion)}
            >
              <span style={{ fontWeight: "700" }}>{beforeQueryText}</span>
              <span style={{ fontWeight: "300" }}>{queryText}</span>
              <span style={{ fontWeight: "700" }}>{afterQueryText}</span>
            </div>
          );
        })}
      </>
    );
  };

  const handleKeyDown = (e: any) => {
    if (e.keyCode !== 13) return;
    else handleSearchClick(e);
  };

  const handleSearchClick = (e: any, flag?: boolean) => {
    if (autocompleteTimeout) {
      clearTimeout(autocompleteTimeout);
      setAutocompleteTimeout(null);
    }
    setAutocompleteSearchText("");
    setIsAutocompleteLoading(false);
    setAutocompleteSuggestions([]);
    setNoResultsFlag(false);
    setSuggestionFlag(true);
    handleSearchByText(e, flag);
  };

  const handleRecentSearchDisplay = () => {
    if (showRecentSearches) {
      return recentSearchArray.map((suggestion: string, index: number) => {
        const showRoundCorners =
          index === recentSearchArray.length - 1 && index >= 4
            ? styles.lastSuggestionItem
            : "";
        return (
          <div
            key={index}
            className={`${styles.recentItem} ${showRoundCorners}`}
            onClick={() => handleRecentSuggestionClick(suggestion)}
          >
            <div className={styles.recentItemText}>{suggestion}</div>
            <div
              className={styles.removeSuggestionButton}
              onClick={(e: any) => handleRemoveSuggestion(e, suggestion)}
            >
              <XIcon />
            </div>
          </div>
        );
      });
    } else {
      return null;
    }
  };

  const displaySeachByText = () => {
    const searchBarButtonClassnames = classNames(styles.searchBarButton, {
      [styles.marginRight]: !imageSearchEnabled,
      [styles.selectedButton]: searchTextParam,
    });

    const containerClassnames = classNames(styles.textualSearchContainer, {
      [styles.expandedTextualSearch]: showRelevanceButton,
    });

    return (
      <div className={containerClassnames}>
        <div className={styles.inputBox}>
          <input
            type="search"
            placeholder={searchPlaceHolder}
            onChange={handleInputChange}
            value={inputText}
            className={styles.searchBarInput}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setIsRecentSearchInFocus(true);
            }}
            ref={inputRef}
          />

          {showRelevanceButton && <RelevanceButton />}

          <div
            className={inputText ? styles.crossBtn : styles.disabledCrossBtn}
            onClick={handleClearSearch}
          >
            <XIcon />
          </div>
        </div>
        <div
          className={searchBarButtonClassnames}
          onClick={(e) => handleSearchClick(e, true)}
        >
          <SearchIcon />
        </div>
      </div>
    );
  };

  const dynamicContainerWidth = () => {
    const minWidth = imageSearchEnabled
      ? "285px"
      : searchEnabled
        ? "285px"
        : "245px";

    return { minWidth };
  };

  useEffect(() => {
    if (searchTextParam && !inputText) {
      setFirstLoad(true);
    }
    setInputText(searchTextParam || "");
    //eslint-disable-next-line
  }, [searchTextParam]);

  useEffect(() => {
    if (isAutoSuggestQueryEnabled) {
      if (!suggestionFlag) {
        if (!inputText) {
          setIsAutocompleteLoading(false);
          setNoResultsFlag(false);
        } else {
          if (!firstLoad) {
            setIsRecentSearchInFocus(false);
            setIsAutocompleteLoading(true);
            const timeoutId: any = setTimeout(() => {
              setAutocompleteSearchText(inputText.trim());
            }, 1000);
            setAutocompleteTimeout(timeoutId);

            return () => {
              clearTimeout(timeoutId);
              setAutocompleteTimeout(null);
            };
          } else if (firstLoad) {
            setFirstLoad(false);
          }
        }
      }
    }
    //eslint-disable-next-line
  }, [inputText, suggestionFlag]);

  useEffect(() => {
    if (isAutoSuggestQueryEnabled) {
      if (autocompleteSearchText) {
        fetchAutocompleteSuggestions(datasetId, autocompleteSearchText) as any;
      } else {
        setAutocompleteSuggestions([]);
      }
    }
    //eslint-disable-next-line
  }, [autocompleteSearchText]);

  useEffect(() => {
    if (isRecentSearchEnabled && isRecentSearchInFocus) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [isRecentSearchInFocus]);

  useEffect(() => {
    //handles ClearFilter click
    if (isClearFiltersCalled) {
      clearAll();
      setIsClearFiltersCalled(false);
    }
    //eslint-disable-next-line
  }, [isClearFiltersCalled]);

  return isUserConfigLoading ? (
    <div className={styles.searchBarLoader}></div>
  ) : (
    <div className={styles.searchWrapper} ref={containerRef}>
      {showEntityTypeLoader ? (
        <div className={styles.entityTypeLoader}></div>
      ) : (
        <EntityButton />
      )}

      <div
        className={styles.searchBarContainer}
        style={dynamicContainerWidth()}
      >
        {searchEnabled ? (
          displaySeachByText()
        ) : (
          <SearchContainerContactBanner loading={isLoading} />
        )}
        {imageSearchEnabled && (
          <ImageSearchButton isImageUploading={isImageUploading} />
        )}
        {showAutocompleteSuggestions && (
          <div
            className={styles.autocompleteDropdown}
            ref={availableSuggestionsRef}
          >
            {handleSuggestions()}
          </div>
        )}
        {showRecentSearches && (
          <div
            className={styles.recentSuggestionsDropdown}
            ref={recentSearchesRef}
          >
            <div className={styles.recentSearchHeader}>
              <div className={styles.recentSearchHeaderTitle}>Recent</div>
            </div>
            {handleRecentSearchDisplay()}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    autocompleteSuggestions: getAutocompleteSuggestions(state),
    recentSearches: getRecentSearches(state),
    isRecentSearchEnabled: getIsRecentSearchEnabled(state),
    isAutoSuggestQueryEnabled: getIsAutoSuggestQueryEnabled(state),
    isAutoCompleteLoading: getAutocompleteLoading(state),
    noResultsFlag: getAutocompleteNoResultsFlag(state),
    isLoading: getContentLoading(state) || getIsCardslistLoading(state),
  };
};
export default connect(mapStatesToProps)(SearchContainer);
